import type { EnclosureKind } from '../level/segments/SegmentDefinition';

/** Length range of a zone (meters of track). */
export interface ZoneLength {
  readonly min: number;
  readonly max: number;
}

/**
 * Night look relative to the day setup of RenderingConfig (sun, IBL, sky, fog). Scales multiply
 * the day values, colors replace them (blended over `transitionSeconds`).
 */
export interface NightLighting {
  readonly sunScale: number;
  /** Moonlight. */
  readonly sunColor: string;
  readonly environmentScale: number;
  /** Brightness of the sky dome (day = 1) and its tint (dark blue night sky). */
  readonly skyLevel: number;
  readonly skyTint: string;
  /** Brightness of the city skyline band (day = 1). */
  readonly skylineLevel: number;
  readonly skylineTint: string;
  /** Fog and clear color – the track fades into the dark. */
  readonly fogColor: string;
  readonly fogStart: number;
  readonly fogEnd: number;
  /** Share of the runner light (0 = off). */
  readonly runnerLight: number;
}

/** Daylight inside an enclosed stretch (tunnel, interior, shell building): scales on top of day/night. */
export interface EnclosureLighting {
  readonly sunScale: number;
  readonly environmentScale: number;
  /** Share of the runner light – the lamps along the walls light the runner. */
  readonly runnerLight: number;
}

/**
 * Day/night zones and enclosed-stretch lighting (Phase 3). The zone schedule is pure data
 * (track distance → day/night, seeded per run); rendering/ZoneLighting.ts blends the scene.
 */
export interface ZonesConfig {
  readonly enabled: boolean;
  readonly schedule: {
    /** The run starts in daylight for this long. */
    readonly firstDay: ZoneLength;
    readonly day: ZoneLength;
    readonly night: ZoneLength;
  };
  /** Duration of the day ↔ night blend (seconds, eased). */
  readonly transitionSeconds: number;
  readonly night: NightLighting;
  readonly enclosures: Readonly<Record<EnclosureKind, EnclosureLighting>>;
  /** Enclosure lighting fades in over this many meters behind each portal (camera position). */
  readonly enclosureFadeMeters: number;
  /**
   * Point light that travels with the runner (site floodlights / tunnel lamps) – lights the
   * track around the player at night and inside tunnels. Always present (intensity 0 by day),
   * so switching it never recompiles shaders.
   */
  readonly runnerLight: {
    readonly color: string;
    /** Full intensity (PBR, physical falloff). */
    readonly intensity: number;
    readonly range: number;
    /** Offset from the track centre at the runner's distance: up and ahead along the track (m). */
    readonly height: number;
    readonly ahead: number;
  };
  /** SEG_22 at night: pick-weight multipliers of light-carrying decorations in night zones (cosmetic). */
  readonly nightDecorationWeights: Readonly<Record<string, number>>;
}

export const ZONES_CONFIG: ZonesConfig = {
  enabled: true,
  schedule: {
    firstDay: { min: 700, max: 1000 },
    day: { min: 800, max: 1200 },
    night: { min: 600, max: 900 },
  },
  transitionSeconds: 5,
  // brighter night (user 2026-10-04: "nicht ganz so dunkel, wenn es Nacht wird") – was sun 0.1, environment 0.14,
  // sky 0.07, skyline 0.22, fog #121826 45–150 m: obstacles read further ahead, still clearly night
  night: {
    sunScale: 0.22,
    sunColor: '#9db4ff',
    environmentScale: 0.3,
    skyLevel: 0.14,
    skyTint: '#5f7fd6',
    skylineLevel: 0.35,
    skylineTint: '#7f93c8',
    fogColor: '#1c2638',
    fogStart: 60,
    fogEnd: 175,
    runnerLight: 1,
  },
  enclosures: {
    TUNNEL: { sunScale: 0.12, environmentScale: 0.22, runnerLight: 0.85 },
    INTERIOR: { sunScale: 0.3, environmentScale: 0.4, runnerLight: 0.3 }, // low 3.8 m ceiling: a stronger runner light leaves a hot spot
    SHELL: { sunScale: 0.75, environmentScale: 0.7, runnerLight: 0.25 },
  },
  enclosureFadeMeters: 5,
  runnerLight: {
    color: '#ffd7a0',
    intensity: 32,
    range: 22,
    height: 2.6,
    ahead: 2.0,
  },
  nightDecorationWeights: {
    ENV_LIGHT_MAST_A: 4,
    ENV_ROADSIDE_YARD_LIT_A: 5, // roadside yard with light mast + generator: ≈ 45 % of the bays at night (day ≈ 15 %)
    ENV_LIGHT_A: 2.5,
    ENV_WARNING_LIGHT_A: 2,
    ENV_GENERATOR_A: 1.5,
  },
};
